import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SupplierOrder {
  id: number;
  order_number: string;
  supplier_name: string;
  order_date: string;
  status: 'draft' | 'pending' | 'sent' | 'cancelled';
  total_items: number;
  total_box_quantity: number;
  total_single_quantity: number;
  created_by_name: string;
  created_by_lastname: string;
  created_at: string;
}

const SupplierOrderList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, hasRole } = useAuth();
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<SupplierOrder | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [searchTerm, statusFilter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/supplier-orders?${params.toString()}`);
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (error: any) {
      setError('Failed to fetch supplier orders');
      console.error('Error fetching supplier orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (order: SupplierOrder) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/supplier-orders/${orderToDelete.id}`);
      await fetchOrders();
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to delete order';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'sent': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const canEdit = hasPermission('supplier_orders.update') || hasRole(['super_admin', 'admin']);

  const canDelete = hasPermission('supplier_orders.delete') || hasRole(['super_admin', 'admin']);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Supplier Orders</Typography>
          {(hasPermission('supplier_orders.create') || hasRole(['super_admin', 'admin'])) && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/supplier-orders/new')}>
              New Supplier Order
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        <Box display="flex" gap={2} mb={3}>
          <TextField
            placeholder="Search by order number or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="center">Box Qty</TableCell>
                <TableCell align="center">Single Qty</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No supplier orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover onClick={() => navigate(`/supplier-orders/${order.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{order.order_number}</Typography>
                    </TableCell>
                    <TableCell>{order.supplier_name}</TableCell>
                    <TableCell>
                      {order.order_date ? format(new Date(order.order_date), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.toUpperCase()}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">{order.total_items || 0}</TableCell>
                    <TableCell align="center">{order.total_box_quantity || 0}</TableCell>
                    <TableCell align="center">{order.total_single_quantity || 0}</TableCell>
                    <TableCell>
                      {`${order.created_by_name || ''} ${order.created_by_lastname || ''}`.trim()}
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.created_at), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => navigate(`/supplier-orders/${order.id}`)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canEdit && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => navigate(`/supplier-orders/${order.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(order)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Supplier Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete order {orderToDelete?.order_number}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierOrderList;
